import { singleton } from 'tsyringe';
import {
  AvailableMapsResponse,
  CleaningScheduleOccurrencesResponse,
  MapGroupsResponse,
  MapsResponse,
  RoutesResponse,
  RoutinesResponse,
  ScheduleClient,
  ScheduleClientAssociateDevicesToMapOptions,
  ScheduleClientListAvailableMapsOptions,
  ScheduleClientListCleaningScheduleOccurrencesOptions,
  ScheduleClientListMapGroupsOptions,
  ScheduleClientListMapsOptions,
  ScheduleClientListRoutesOptions,
  ScheduleClientListRoutinesOptions,
} from './ScheduleClient';
import {
  type AssociateDevicesToMapResponse,
  type CleaningScheduleV2,
  type CreateScheduleInput,
  type DeleteScheduleInput,
  type UpdateScheduleInput,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

export type ScheduleServiceListCleaningScheduleOccurrencesOptions =
  ScheduleClientListCleaningScheduleOccurrencesOptions;
export type ScheduleServiceListRoutinesOptions = ScheduleClientListRoutinesOptions;
export type ScheduleServiceListRoutesOptions = ScheduleClientListRoutesOptions;
export type ScheduleServiceListMapsOptions = ScheduleClientListMapsOptions;
export type ScheduleServiceListAvailableMapsOptions = ScheduleClientListAvailableMapsOptions;
export type ScheduleServiceListMapGroupsOptions = ScheduleClientListMapGroupsOptions;
export type ScheduleServiceCreateScheduleOptions = CreateScheduleInput;
export type ScheduleServiceUpdateScheduleOptions = UpdateScheduleInput;
export type ScheduleServiceDeleteScheduleOptions = DeleteScheduleInput;
export type ScheduleServiceAssociateDevicesToMapOptions = ScheduleClientAssociateDevicesToMapOptions;

@singleton()
export class ScheduleService {
  constructor(private scheduleClient: ScheduleClient) {}

  public listCleaningScheduleOccurrences = async ({
    materialNumber,
    serialNumber,
    period,
  }: ScheduleServiceListCleaningScheduleOccurrencesOptions): Promise<CleaningScheduleOccurrencesResponse> => {
    const { data } = await this.scheduleClient.listCleaningScheduleOccurrences({
      materialNumber,
      serialNumber,
      period,
    });

    return data;
  };

  public listRoutines = async ({
    materialNumber,
    serialNumber,
    anonymous,
    deviceTypes,
    routeId,
    first,
    orderBy,
  }: ScheduleServiceListRoutinesOptions): Promise<RoutinesResponse> => {
    const { data } = await this.scheduleClient.listRoutines({
      materialNumber,
      serialNumber,
      anonymous,
      deviceTypes,
      routeId,
      first,
      orderBy,
    });

    return data;
  };

  public listRoutes = async ({
    materialNumber,
    serialNumber,
    deviceTypes,
    first,
    orderBy,
  }: ScheduleServiceListRoutesOptions): Promise<RoutesResponse> => {
    const { data } = await this.scheduleClient.listRoutes({
      materialNumber,
      serialNumber,
      deviceTypes,
      first,
      orderBy,
    });

    return data;
  };

  public listMaps = async (options: ScheduleServiceListMapsOptions): Promise<MapsResponse> => {
    const { data } = await this.scheduleClient.listMaps(options);

    return data;
  };

  public listAvailableMaps = async (
    options: ScheduleServiceListAvailableMapsOptions
  ): Promise<AvailableMapsResponse> => {
    const { data } = await this.scheduleClient.listAvailableMaps(options);

    return data;
  };

  public listMapGroups = async (options: ScheduleServiceListMapGroupsOptions): Promise<MapGroupsResponse> => {
    const { data } = await this.scheduleClient.listMapGroups(options);

    return data;
  };

  public createSchedule = async (input: ScheduleServiceCreateScheduleOptions): Promise<CleaningScheduleV2> => {
    const { data, errors } = await this.scheduleClient.createSchedule(input);

    if (!data?.createSchedule) {
      throw new Error(errors?.[0]?.message || 'Failed to create schedule');
    }

    return data.createSchedule;
  };

  public updateSchedule = async (input: ScheduleServiceUpdateScheduleOptions): Promise<CleaningScheduleV2> => {
    const { data, errors } = await this.scheduleClient.updateSchedule(input);

    if (!data?.updateSchedule) {
      throw new Error(errors?.[0]?.message || 'Failed to update schedule');
    }

    return data.updateSchedule;
  };

  public deleteSchedule = async (input: ScheduleServiceDeleteScheduleOptions): Promise<CleaningScheduleV2> => {
    const { data, errors } = await this.scheduleClient.deleteSchedule(input);

    if (!data?.deleteSchedule) {
      throw new Error(errors?.[0]?.message || 'Failed to delete schedule');
    }

    return data.deleteSchedule;
  };

  public associateDevicesToMap = async (
    input: ScheduleServiceAssociateDevicesToMapOptions
  ): Promise<AssociateDevicesToMapResponse> => {
    const { data, errors } = await this.scheduleClient.associateDevicesToMap(input);

    if (!data?.associateDevicesToMap?.success) {
      throw new Error(errors?.[0]?.message || 'Failed to associate devices to map');
    }

    return data.associateDevicesToMap;
  };
}
